import React, { useState } from 'react';
import { Card, Alert } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import ObjectChangeSetsList from './ObjectChangeSetsList';
import ButtonControls from '@rd-web-markets/shared/dist/technical_review/ButtonControls';
import { useTranslation } from 'react-i18next';
import ObjectChangeSetsListWithSnapshots from '@rd-web-markets/shared/dist/technical_review/ObjectChangeSetsListWithSnapshots';
import AssignChangeSetModal from './AssignChangeSetModal';
import ChecklistModal from './ChecklistModal';
import checklistTemplateService from 'src/pages/admin/checklist_template/checklist_template.service';
import claimGroupChangeSetsService from '@rd-web-markets/shared/dist/services/claim_groups/claim_group_change_sets.service';
import { Loading } from '@rd-web-markets/shared/dist/util';

const ClaimGroupTechnicalManagerQAReviews = ({
  claimGroup,
  loading,
  company,
  createChangeSet,
  approveChangeSet,
  rejectChangeSet,
  finalizeChangeSet
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [projectsUnderTechnicalReview, setProjectsUnderTechnicalReview] = useState(claimGroup.has_active_project_change_sets);
  const [assignModalShow, setAssignModalShow] = useState(false);
  const [checklistModalShow, setChecklistModalShow] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState('rnd_only');
  const [checklistAnswers, setChecklistAnswers] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);

  const {
    activeChangeSet,
    changeSets,
    fetchChangeSets,
  } = claimGroupChangeSetsService.useCrud(claimGroup.id);

  const reviewedClaimGroup = {
    ...claimGroup,
    active_change_set: activeChangeSet,
    change_sets: changeSets,
  };

  const isUkMarket = process.env.PUBLIC_URL !== '/de';

  const {
    templates,
  } = isUkMarket ? checklistTemplateService.useCrud(setChecklistLoading) : { templates: [] };

  const currentTemplate = templates && templates.length > 0 ? templates[0] : null;

  const handleReportTypeChange = (reportType) => {
    console.log('Report type changed to:', reportType);
    setSelectedReportType(reportType);
  };

  const handleStartReviewClick = () => {
    console.log('Start review clicked with type:', selectedReportType);
    
    if (isUkMarket && (selectedReportType === 'cost_only' || selectedReportType === 'both')) {
      if (!currentTemplate) {
        dispatch(handleError({ description: t('checklist_template_not_found') }));
        return;
      }
      setChecklistModalShow(true);
    } else {
      setAssignModalShow(true);
    }
  };

  const handleChecklistSubmit = (answers) => {
    console.log('Checklist submitted with answers:', answers);
    setChecklistAnswers(answers);
    setChecklistModalShow(false);
    setAssignModalShow(true);
  };

  const createChangeSetAndRefresh = async (...args) => {
    await createChangeSet(...args);
    await fetchChangeSets();
  };

  const handleAssignSubmit = async (assignedUserId, send_email) => {
    console.log('Assignment submitted:', {
      assignedUserId,
      send_email,
      selectedReportType,
      checklistAnswersCount: checklistAnswers.length
    });

    try {
      if (isUkMarket) {
        await createChangeSetAndRefresh(
          claimGroup, 
          'ClaimGroup', 
          'consultant_review', 
          assignedUserId, 
          send_email,
          selectedReportType,
          checklistAnswers
        );
      } else {
        await createChangeSetAndRefresh(
          claimGroup, 
          'ClaimGroup', 
          'consultant_review', 
          assignedUserId, 
          send_email
        );
      }
      
      setAssignModalShow(false);
      setChecklistAnswers([]);
      setSelectedReportType('rnd_only');
    } catch (error) {
      console.error('Error creating change set:', error);
      dispatch(handleError(error));
    }
  };

  const finalizeChangeSetAndRefresh = async (reviewedObject) => {
    await finalizeChangeSet(reviewedObject);
    await fetchChangeSets();
  };

  const approveChangeSetAndRefresh = async (reviewedObject) => {
    await approveChangeSet(reviewedObject);
    await fetchChangeSets();
  };

  const rejectChangeSetAndRefresh = async (reviewedObject) => {
    await rejectChangeSet(reviewedObject);
    await fetchChangeSets();
  };

  if (changeSets === undefined) {
    return <Loading />;
  }

  return (
    <>
      <div className="ps-4 pe-4">
        <h2 className="text-primary mb-4 mt-3">{t('manager_review')}</h2>
        <Card className='shadow border-0'>
          <Card.Body>
            {claimGroup ? (
              <>
                {projectsUnderTechnicalReview.length > 0 ? (
                  <Alert variant='info'>
                    {t('following_projects_are_under_review')}: {projectsUnderTechnicalReview.join(', ')}
                  </Alert>
                ) : (
                  <ButtonControls
                    reviewedObject={reviewedClaimGroup}
                    reviewedObjectClass={'ClaimGroup'}
                    loading={loading}
                    claimGroup={reviewedClaimGroup}
                    company={company}
                    setModalShow={handleStartReviewClick}
                    createChangeSet={createChangeSetAndRefresh}
                    finalizeChangeSet={finalizeChangeSetAndRefresh}
                    approveChangeSet={approveChangeSetAndRefresh}
                    rejectChangeSet={rejectChangeSetAndRefresh}
                    buttonName={t('internal_rnd_review')}
                    showReportTypeSelector={isUkMarket}
                    onReportTypeChange={handleReportTypeChange}
                    selectedReportType={selectedReportType}
                  />
                )}

                <ObjectChangeSetsListWithSnapshots
                  object={reviewedClaimGroup}
                  reviewType={'consultant_review'}
                  resetData={fetchChangeSets}
                />
              </>
            ) : (
              <span>{t('please_create_a_claim_group')}</span>
            )}
          </Card.Body>
        </Card>

        {/* Only render ChecklistModal for UK market */}
        {isUkMarket && (
          <ChecklistModal
            show={checklistModalShow}
            onHide={() => {
              setChecklistModalShow(false);
              setChecklistAnswers([]);
            }}
            checklistTemplate={currentTemplate}
            onSubmit={handleChecklistSubmit}
          />
        )}

        <AssignChangeSetModal
          show={assignModalShow}
          onHide={() => {
            setAssignModalShow(false);
            setChecklistAnswers([]);
          }}
          claimGroup={claimGroup}
          title={t('assign_to_user')}
          onSubmit={handleAssignSubmit}
        />
      </div>
    </>
  );
};

export default ClaimGroupTechnicalManagerQAReviews;
